import { useCallback, useRef, useState } from "react";
import { IntegrationBaseService } from "@/features/Integrations/services/integrationsBaseService";

export type TitleAvailability = "idle" | "checking" | "available" | "taken";

/**
 * Checagem do title no blur (AB#87541): o title identifica a instalação e é único na
 * organização. É só aviso antecipado — a api garante a unicidade na gravação e responde
 * `E_INTEGRATION_TITLE_TAKEN`, então uma falha AQUI volta para `idle` e deixa o submit
 * decidir, em vez de travar o formulário por causa de uma consulta auxiliar.
 */
export function useTitleAvailability(excludeId?: string, organizationId?: string) {
  const [status, setStatus] = useState<TitleAvailability>("idle");
  // Só a resposta da ÚLTIMA consulta vale: digitar rápido e sair do campo duas vezes não
  // pode deixar a primeira resposta sobrescrever a segunda.
  const lastRequest = useRef(0);

  const check = useCallback(
    async (title: string) => {
      const value = title.trim();
      const request = ++lastRequest.current;
      if (!value) {
        setStatus("idle");
        return;
      }
      setStatus("checking");
      try {
        const available = await IntegrationBaseService.isTitleAvailable(value, excludeId, organizationId);
        if (request === lastRequest.current) setStatus(available ? "available" : "taken");
      } catch {
        if (request === lastRequest.current) setStatus("idle");
      }
    },
    [excludeId, organizationId],
  );

  const reset = useCallback(() => {
    lastRequest.current += 1;
    setStatus("idle");
  }, []);

  return { status, check, reset };
}
